'use strict';

/**
 * Tab Hash Module
 * 用 URL hash 控制 Bootstrap 5 Tab 的顯示
 *
 * 功能：
 * 1. 頁面載入時，根據 URL hash 開啟對應的 tab
 * 2. 點擊 tab 時，更新 URL hash
 * 3. 瀏覽器前進/後退時，同步切換 tab
 *
 * 使用方式：
 * - URL: page.html#payer → 開啟 payer tab
 * - URL: page.html#user → 開啟 user tab
 */

import { Tab } from 'bootstrap';

/**
 * 初始化 Tab Hash 功能
 * @param {Object} options - 設定選項
 * @param {string} options.tabSelector - Tab 按鈕的選擇器 (預設: '[data-bs-toggle="tab"]')
 * @param {Function} options.getHashFromTab - 從 tab 元素取得 hash 的函式
 * @param {Function} options.getTabFromHash - 從 hash 取得 tab 元素的函式
 */
export function initTabHash(options = {}) {
  const {
    tabSelector = '[data-bs-toggle="tab"]',
    // 預設：從 tab 的 data-bs-target 取得 hash (移除 -tab-pane 後綴)
    getHashFromTab = (tab) => {
      const target = tab.getAttribute('data-bs-target');
      if (target) {
        // #payer-tab-pane → payer
        return target.replace('#', '').replace('-tab-pane', '');
      }
      return null;
    },
    // 預設：從 hash 找到對應的 tab 按鈕
    getTabFromHash = (hash) => {
      if (!hash) return null;
      // payer → #payer-tab
      const tabId = `#${hash}-tab`;
      return document.querySelector(tabId);
    }
  } = options;

  const tabs = document.querySelectorAll(tabSelector);

  if (tabs.length === 0) return;

  /**
   * 根據 hash 啟動對應的 tab
   */
  function activateTabFromHash() {
    const hash = window.location.hash.slice(1); // 移除 #
    if (!hash) return;

    const tabElement = getTabFromHash(hash);
    if (tabElement) {
      const tabInstance = Tab.getOrCreateInstance(tabElement);
      tabInstance.show();
    }
  }

  /**
   * 更新 URL hash (不觸發 scroll)
   */
  function updateHash(hash) {
    if (hash) {
      history.replaceState(null, '', `#${hash}`);
    }
  }

  // 監聽 tab 切換事件，更新 URL hash
  tabs.forEach((tab) => {
    tab.addEventListener('shown.bs.tab', (event) => {
      const hash = getHashFromTab(event.target);
      if (hash) {
        updateHash(hash);
      }
    });
  });

  // 監聽瀏覽器前進/後退
  window.addEventListener('hashchange', () => {
    activateTabFromHash();
  });

  // 頁面載入時，根據 hash 開啟對應 tab
  activateTabFromHash();
}
